import apiClient from '../apiClient';

// Types matching the backend entities

export interface ChronicDisease {
  id: string;
  name: string;
  icdCode?: string;
  onsetDate?: string;
  status: 'active' | 'resolved' | 'managed';
  severity?: 'mild' | 'moderate' | 'severe';
  notes?: string;
}

export interface Allergy {
  id: string;
  allergen: string;
  type: 'drug' | 'food' | 'environmental' | 'other';
  reaction: string;
  severity: 'mild' | 'moderate' | 'severe' | 'life-threatening';
  onsetDate?: string;
  isActive: boolean;
  notes?: string;
}

export interface Surgery {
  id: string;
  procedure: string;
  date: string;
  hospital?: string;
  surgeon?: string;
  outcome?: 'successful' | 'complications' | 'ongoing';
  notes?: string;
}

export interface FamilyHistoryItem {
  id: string;
  relation: 'mother' | 'father' | 'sibling' | 'grandparent' | 'aunt_uncle' | 'other';
  condition: string;
  ageAtOnset?: number;
  isDeceased?: boolean;
  ageAtDeath?: number;
  notes?: string;
}

export interface PastCondition {
  id: string;
  condition: string;
  yearDiagnosed?: number;
  yearResolved?: number;
  notes?: string;
}

export interface Immunization {
  id: string;
  vaccine: string;
  date: string;
  boosterDue?: string;
  notes?: string;
}

export interface MedicalHistory {
  id: string;
  patientId: string;
  chronicDiseases: ChronicDisease[];
  allergies: Allergy[];
  surgeries: Surgery[];
  familyHistory: FamilyHistoryItem[];
  pastConditions: PastCondition[];
  immunizations: Immunization[];
  notes?: string;
  lastReviewedAt?: string;
  lastReviewedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MedicalHistorySummary {
  activeChronicDiseases: string[];
  activeAllergies: { allergen: string; severity: string }[];
  recentSurgeries: string[];
  significantFamilyHistory: string[];
}

export interface CreateMedicalHistoryDto {
  patientId: string;
  chronicDiseases?: ChronicDisease[];
  allergies?: Allergy[];
  surgeries?: Surgery[];
  familyHistory?: FamilyHistoryItem[];
  pastConditions?: PastCondition[];
  immunizations?: Immunization[];
  notes?: string;
}

export interface UpdateMedicalHistoryDto {
  chronicDiseases?: ChronicDisease[];
  allergies?: Allergy[];
  surgeries?: Surgery[];
  familyHistory?: FamilyHistoryItem[];
  pastConditions?: PastCondition[];
  immunizations?: Immunization[];
  notes?: string;
}

export const medicalHistoryApi = {
  // Get medical history for a patient
  getByPatientId: async (patientId: string): Promise<MedicalHistory | null> => {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: MedicalHistory;
      }>(`/medical-history/patient/${patientId}`);
      return response.data.data;
    } catch (error: unknown) {
      if ((error as { response?: { status?: number } }).response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Get summary for AI context
  getSummary: async (patientId: string): Promise<MedicalHistorySummary> => {
    const response = await apiClient.get<{
      success: boolean;
      data: MedicalHistorySummary;
    }>(`/medical-history/patient/${patientId}/summary`);
    return response.data.data;
  },

  // Create medical history
  create: async (data: CreateMedicalHistoryDto): Promise<MedicalHistory> => {
    const response = await apiClient.post<{
      success: boolean;
      data: MedicalHistory;
    }>('/medical-history', data);
    return response.data.data;
  },

  // Update medical history
  update: async (id: string, data: UpdateMedicalHistoryDto): Promise<MedicalHistory> => {
    const response = await apiClient.patch<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/${id}`, data);
    return response.data.data;
  },

  // Mark as reviewed
  markReviewed: async (id: string): Promise<MedicalHistory> => {
    const response = await apiClient.post<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/${id}/review`);
    return response.data.data;
  },

  // Chronic diseases
  addChronicDisease: async (patientId: string, disease: Omit<ChronicDisease, 'id'> & { id?: string }): Promise<MedicalHistory> => {
    const diseaseWithId = {
      ...disease,
      id: disease.id || crypto.randomUUID(),
    };
    const response = await apiClient.post<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/chronic-disease`, diseaseWithId);
    return response.data.data;
  },

  removeChronicDisease: async (patientId: string, diseaseId: string): Promise<MedicalHistory> => {
    const response = await apiClient.delete<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/chronic-disease/${diseaseId}`);
    return response.data.data;
  },

  // Allergies
  addAllergy: async (patientId: string, allergy: Omit<Allergy, 'id'> & { id?: string }): Promise<MedicalHistory> => {
    const allergyWithId = {
      ...allergy,
      id: allergy.id || crypto.randomUUID(),
    };
    const response = await apiClient.post<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/allergy`, allergyWithId);
    return response.data.data;
  },

  removeAllergy: async (patientId: string, allergyId: string): Promise<MedicalHistory> => {
    const response = await apiClient.delete<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/allergy/${allergyId}`);
    return response.data.data;
  },

  // Surgeries
  addSurgery: async (patientId: string, surgery: Omit<Surgery, 'id'> & { id?: string }): Promise<MedicalHistory> => {
    const surgeryWithId = {
      ...surgery,
      id: surgery.id || crypto.randomUUID(),
    };
    const response = await apiClient.post<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/surgery`, surgeryWithId);
    return response.data.data;
  },

  removeSurgery: async (patientId: string, surgeryId: string): Promise<MedicalHistory> => {
    const response = await apiClient.delete<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/surgery/${surgeryId}`);
    return response.data.data;
  },

  // Family history
  addFamilyHistoryItem: async (patientId: string, item: Omit<FamilyHistoryItem, 'id'> & { id?: string }): Promise<MedicalHistory> => {
    const itemWithId = {
      ...item,
      id: item.id || crypto.randomUUID(),
    };
    const response = await apiClient.post<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/family-history`, itemWithId);
    return response.data.data;
  },

  removeFamilyHistoryItem: async (patientId: string, itemId: string): Promise<MedicalHistory> => {
    const response = await apiClient.delete<{
      success: boolean;
      data: MedicalHistory;
    }>(`/medical-history/patient/${patientId}/family-history/${itemId}`);
    return response.data.data;
  },
};
